// Editable site copy. Everything a non-developer would want to change lives here.

export const homeCopy = {
  hero: {
    eyebrow: "Children’s breakfast & lunch subscription • Pune",
    headline: "One less meal to plan. One better meal in their day.",
    support:
      "A rotating three-week menu of fresh, familiar Indian breakfasts and lunches created for growing children. Choose breakfast, lunch or both, with clear ingredients, age-based portions and weekly or monthly subscriptions.",
    primaryCta: { label: "See All Our Meals", href: "/meals" },
    secondaryCta: { label: "Start Your Plan", href: "/get-started" },
    trustLine: [
      "Freshly prepared",
      "Child-friendly Indian meals",
      "Clear nutrition information",
      "Breakfast, lunch or both",
    ],
  },
  problem: {
    headline: "You should not have to choose between convenience and care.",
    copy:
      "Feeding a child well requires planning, shopping, preparation, packing and constant decisions. Mothers by Mothers is designed to make one important part of family life easier without asking parents to lower their standards.",
    cards: [
      {
        title: "Less daily planning",
        copy: "A rotating menu means fewer decisions to make each morning.",
        icon: "CalendarCheck",
      },
      {
        title: "Food made with children in mind",
        copy: "Familiar meals, gentle Indian flavours and age-based portions.",
        icon: "HeartHandshake",
      },
      {
        title: "Clear and trustworthy",
        copy:
          "Parents can review ingredients, nutrition and allergen information before ordering.",
        icon: "ScrollText",
      },
    ],
  },
  product: {
    headline: "Meet the lunch plan",
    copy:
      "A wholesome three-week rotating lunch programme built around familiar Indian food, gentle flavours and ingredients selected with growing children in mind — with a traditional Indian breakfast collection you can add alongside it.",
    benefits: [
      {
        title: "15 lunches, 10 breakfasts",
        copy: "Five meals per sitting each week, rotating so no week repeats itself.",
        icon: "Repeat",
      },
      {
        title: "Breakfast, lunch or both",
        copy: "Choose the meals you want covered, then a week or the full three-week cycle.",
        icon: "LayoutGrid",
      },
      {
        title: "Two age-based portion options",
        copy: "Choose the portion band that fits your child: 2–4, 5–7, 8–10 or 11+.",
        icon: "Ruler",
      },
      {
        title: "Dietary and allergy support",
        copy:
          "Share your child’s allergies, restrictions and food preferences before ordering.",
        icon: "ShieldCheck",
      },
    ],
    cta: { label: "Explore All Our Meals", href: "/meals" },
  },
  steps: [
    {
      title: "Tell us about your child",
      copy: "Add age, preferences, allergies and dietary restrictions.",
    },
    { title: "Choose a plan", copy: "Breakfast, lunch or both — for a week or the full cycle." },
    {
      title: "Confirm delivery",
      copy: "Choose home or school delivery where available.",
    },
    {
      title: "Receive fresh lunches",
      copy: "Meals are delivered according to the agreed schedule.",
    },
  ],
  standards: {
    headline: "What goes into every lunch matters.",
    cards: [
      {
        title: "Fresh preparation",
        copy:
          "Meals are prepared according to the confirmed kitchen and delivery schedule.",
        icon: "Sunrise",
      },
      {
        title: "Gentle Indian flavours",
        copy: "Familiar food created without unnecessarily heavy spice.",
        icon: "Soup",
      },
      {
        title: "Clear ingredients",
        copy: "Parents can review what goes into each dish.",
        icon: "ListChecks",
      },
      {
        title: "Age-based portions",
        copy: "Four portion bands: 2–4, 5–7, 8–10 and 11+.",
        icon: "Scale",
      },
      {
        title: "Thoughtful meal combinations",
        copy: "Menus combine grains, lentils, vegetables and accompaniments.",
        icon: "Salad",
      },
      {
        title: "Dietary support",
        copy: "Parents can share allergies, restrictions and food preferences.",
        icon: "ShieldCheck",
      },
      {
        title: "Pest-free and chemical-free",
        copy:
          "Grains, staples and vegetables chosen pest-free, chemical-free and fertiliser-free — not just labelled organic.",
        icon: "Sprout",
      },
      {
        title: "A note for you and one for them",
        copy:
          "Every meal comes with ingredients and nutrition for you, and a fun note for your child about what it does for them.",
        icon: "ScrollText",
      },
      {
        title: "Plastic-free packaging",
        copy: "Completely plastic-free, biodegradable and eco-friendly materials.",
        icon: "Recycle",
      },
    ],
    cta: { label: "Read Our Food Standards", href: "/food-standards" },
  },
  breakfast: {
    title: "The mornings our grandmothers got right.",
    intro:
      "Traditional Indian breakfasts for growing children — regional porridges, steamed fruit, sprouted millets and warm milk bowls, plus a few modern favourites.",
  },
  founder: {
    headline: "Built by a mother, driven by love, backed by science.",
    copy:
      "Mothers by Mothers began with Urvashi's own experience of becoming a mother: a mountain of questions about what to feed her child, no clear answers, and the relentless guilt that she was never doing quite enough. Every other mother she spoke to described the same thing. So instead of waiting for someone else to solve it, she built the service they all needed — and partnered with paediatric nutritionists to make sure it stands up.",
    cta: { label: "Read Urvashi’s Story", href: "/our-story" },
    imageCaption: "Placeholder — a photograph of Urvashi and Simba will be added here.",
  },
  purpose: {
    headline: "It takes a village — we're yours.",
    copy:
      "Mothers are expected to nourish the next generation, yet their labour is rarely supported. Alongside feeding your child well, we create paid, meaningful work for women who were pushed out of the workforce by childcare.",
    cta: { label: "Learn About the Vision", href: "/mothers-supporting-mothers" },
  },
  testimonials: [
    { placeholder: "Parent reviews appear here as families share them." },
    { placeholder: "We publish only real, verified words." },
    { placeholder: "School feedback appears here too." },
  ],
};

export const painPoints = [
  "What should my child eat tomorrow?",
  "Will they actually eat the meal?",
  "Is the food freshly prepared?",
  "Is it too spicy?",
  "Does it contain too much sugar or oil?",
  "What ingredients are being used?",
  "What if my child has an allergy?",
  "Is the portion suitable for their age?",
  "Can I trust the kitchen?",
  "Will delivery be reliable?",
];

export const howItWorksDetailed = [
  {
    title: "Create the child profile",
    copy:
      "We collect the details that let us prepare the right lunch for the right child.",
    points: [
      "Parent details",
      "Child name",
      "Age and age category",
      "School (if applicable)",
      "Vegetarian preference",
      "Allergies",
      "Dietary restrictions",
      "Food dislikes",
      "Spice preference",
    ],
  },
  {
    title: "Choose the subscription",
    copy: "Pick which meals you want covered, then how long for.",
    points: [
      "Breakfast — 5 breakfasts a week",
      "Lunch — 5 lunches a week",
      "Breakfast + lunch — 10 meals a week",
      "Weekly, or the complete three-week cycle",
      "Change plan between cycles",
    ],
  },
  {
    title: "Confirm delivery",
    copy: "Home or school delivery, subject to area availability.",
    points: [
      "Check your pincode",
      "Choose home or school delivery",
      "Select your school where available",
      "Confirm your start date",
    ],
  },
  {
    title: "Review the menu",
    copy: "See upcoming lunches with ingredient and nutrition information.",
    points: [
      "Upcoming meals for the week",
      "Ingredients for every dish",
      "Allergen information",
      "Storage and reheating guidance",
    ],
  },
  {
    title: "Pay securely",
    copy:
      "Razorpay will handle payments once the account is activated. Until then our team confirms your subscription with you directly.",
    points: ["Weekly or monthly payment", "Coupon and referral codes", "Invoice on request"],
  },
  {
    title: "Receive confirmations",
    copy: "Confirmations by email and WhatsApp so nothing is left uncertain.",
    points: ["Order confirmation", "Delivery schedule", "Change and pause notices"],
  },
];

export const faqs = [
  {
    q: "What age groups do you serve?",
    a: "Our menu is portioned for four age bands: 2–4, 5–7, 8–10 and 11+. You choose the portion category for each child when you create their profile.",
  },
  {
    q: "What plans can I choose from?",
    a: "Six combinations. First choose the meals — breakfast only, lunch only, or breakfast and lunch together. Then choose the length — a single week, or the complete three-week cycle. Every plan includes age-based portions, your child’s dietary profile, and a parent note and child note with each meal.",
  },
  {
    q: "What is included in the weekly plan?",
    a: "One week of whichever meals you chose: 5 breakfasts, 5 lunches, or 10 meals if you took both. It is the simplest way to try the service before committing to a full cycle.",
  },
  {
    q: "What is included in the monthly plan?",
    a: "The complete three-week rotation of whichever meals you chose — 15 breakfasts, 15 lunches, or 30 meals for breakfast and lunch together — so no meal repeats within the cycle.",
  },
  {
    q: "Can I add breakfast to an existing lunch plan?",
    a: "Yes. You can switch to the breakfast and lunch plan at the start of your next cycle. Message our team and we will move you across.",
  },
  {
    q: "Is every meal vegetarian?",
    a: "Every lunch on the three-week menu is vegetarian. On the breakfast menu, the scrambled eggs dish is marked “Contains egg” and the mung dal chilla is marked “Egg filling optional” — the egg filling is never sent unless you ask for it. Many meals contain dairy such as ghee, curd or butter, and that is listed on every dish.",
  },
  {
    q: "Can you support allergies?",
    a: "We collect allergy and dietary information before any order is confirmed, and every meal card lists common allergens. If your child has a severe or life-threatening allergy, please speak to our team before ordering so we can tell you honestly whether we can serve your child safely.",
  },
  {
    q: "How are portions decided?",
    a: "Portions are grouped into four age bands — 2–4, 5–7, 8–10 and 11+ — using the portion guidance in our menu plan.",
  },
  {
    q: "Where do you deliver?",
    a: "We deliver across selected Pune areas. Use the pincode checker to see whether your area is included.",
  },
  {
    q: "Do you deliver to schools?",
    a: "Yes, to selected schools. Availability depends on the school’s permission and our delivery route — tell us your child’s school and we will confirm it for you.",
  },
  {
    q: "When are meals prepared?",
    a: "Meals are prepared fresh in the morning on the day of delivery, never cooked in advance and stored.",
  },
  {
    q: "How should meals be stored?",
    a: "If a meal is not eaten soon after delivery, keep it refrigerated below 5°C and consume it the same day. Storage guidance is printed on each meal card.",
  },
  {
    q: "Can meals be reheated?",
    a: "Yes. Each meal card includes simple reheating instructions. Items such as raita and salads are meant to be served cold.",
  },
  {
    q: "Can I pause my subscription?",
    a: "Pausing from the customer account is on its way. For now, message our team on WhatsApp and we will pause your deliveries for you.",
  },
  {
    q: "Can I skip a day?",
    a: "Yes. Tell us before the daily cut-off time and we will skip that delivery. Skipping from the customer account is on its way.",
  },
  {
    q: "Can I change my delivery address?",
    a: "Yes. Address changes are handled from your account or by messaging our team, subject to the new area being serviceable.",
  },
  {
    q: "Can I cancel?",
    a: "Yes. Cancellation follows our Cancellation and Refund Policy. You will never be locked into a long contract.",
  },
  {
    q: "Are the meals paediatrician-approved?",
    a: "We do not make that claim. The menu is thoughtfully planned for growing children. If and when a qualified paediatrician formally reviews and approves a specific menu version, we will say so with their name and qualifications.",
  },
  {
    q: "Do you use added sugar?",
    a: "Sweet items on the menu use jaggery rather than refined white sugar. Some ingredients contain naturally occurring sugars. We will publish a precise statement once every recipe and supplier is confirmed.",
  },
  {
    q: "Do you offer breakfast?",
    a: "Yes — a breakfast collection has been finalised, including traditional dishes such as Gujarati raab, rice and millet milk porridge and saffron-almond porridge, alongside amaranth and oat power bowls and a few modern favourites. Breakfast can be taken on its own or alongside lunch.",
  },
  {
    q: "Is the breakfast menu vegetarian too?",
    a: "Almost all of it. One dish — scrambled spinach and cheese eggs with multigrain toast — contains egg, and the mung dal chilla can be filled with either paneer or egg. Egg dishes are strictly opt-in, so a vegetarian household will never be sent egg by default.",
  },
  {
    q: "Why do some meals not show calorie and protein figures?",
    a: "Because those recipes were added recently and the values have not been properly worked out yet. Our standard is caloric and ingredient transparency, so we would rather leave a figure blank than publish an estimate. Every meal card is updated as soon as the numbers are confirmed.",
  },
  {
    q: "What will the food arrive in?",
    a: "All of our packaging is completely plastic-free and made from biodegradable, eco-friendly materials.",
  },
  {
    q: "What is the parent note and the note for children?",
    a: "Every meal travels with two short notes. The parent note sets out the ingredients used and the nutritional and caloric value. The child’s note is a simple, fun line explaining how the meal gives them strength and energy to play, so they connect with what they are eating.",
  },
  {
    q: "How can I speak to the team?",
    a: "Message us on WhatsApp, email us, or use the contact form. A real person from our team will reply.",
  },
];

export const foodStandards = {
  intro:
    "These are the standards we hold ourselves to. Where something is still being confirmed operationally, we say so rather than making a claim we cannot support yet.",
  pillars: [
    {
      title: "Utmost quality and uncompromised ingredients",
      icon: "Sprout",
      points: [
        "Clean, pest-free and chemical-free: we go beyond simply labelling food organic. Every grain, staple, grocery item and vegetable we use is pest-free, chemical-free and fertiliser-free, to support your child’s gut health and overall nutrition.",
        "Traditional Indian cooking practices: food is cooked the way it is meant to be — prepared with pure ghee and rooted in authentic Indian methods, so there is real warmth and nourishment in every bite.",
      ],
    },
    {
      title: "Transparency and conscious nutrition",
      icon: "ScrollText",
      points: [
        "Parent note and caloric transparency: every meal arrives with a clear note for parents setting out the ingredients used and the nutritional and caloric value provided.",
        "A fun, encouraging note for children: each meal also includes a simple note explaining how it gives them strength, energy and power to play, so children connect with what they are eating.",
      ],
    },
    {
      title: "Child-friendly presentation",
      icon: "Smile",
      points: [
        "Healthy food does not have to look boring. We make our meals engaging, fun and child-friendly so children are excited to eat, while staying entirely true to pure, unadulterated food.",
      ],
    },
    {
      title: "100% plastic-free, eco-conscious packaging",
      icon: "Recycle",
      points: [
        "We care about the world our children will inherit. All of our packaging is completely plastic-free and made from biodegradable, eco-friendly materials.",
      ],
    },
    {
      title: "Our unwavering promise",
      icon: "HeartHandshake",
      points: [
        "This is not just a service — it is a mission rooted in personal experience and parental care. We promise never to compromise on quality, safety or nutrition, so you have complete peace of mind with every meal.",
      ],
    },
  ],
  sections: [
    {
      title: "How meals are planned",
      points: [
        "Each lunch combines a grain, a lentil or legume, a vegetable and an accompaniment.",
        "Flavours are kept gentle so that food is familiar rather than challenging.",
        "The three-week rotation avoids repetition without introducing unfamiliar food.",
        "Portions are grouped by age category rather than served as reduced adult meals.",
      ],
    },
    {
      title: "Ingredients and transparency",
      points: [
        "Every meal card lists its ingredients and common allergens.",
        "Approximate nutrition is published for each meal.",
        "Where a value has not been confirmed, we leave it blank instead of estimating.",
        "Storage and reheating guidance is provided with every meal.",
      ],
    },
    {
      title: "Kitchen and handling",
      points: [
        "Meals are prepared to the confirmed kitchen and delivery schedule.",
        "Hygiene procedures, temperature control and packaging standards are documented before any new kitchen or cook joins.",
        "Quality checks are carried out before dispatch.",
      ],
    },
  ],
  allergyDisclaimer:
    "Our kitchen handles milk products, wheat, peanuts, tree nuts, sesame, coconut, egg and other common allergens. We cannot guarantee that a meal is free from traces of an allergen. If your child has a severe or life-threatening allergy, please contact our team before ordering.",
};

export const ourStory = {
  headline: "Built by a mother, driven by love, backed by science.",
  opening:
    "When I became a mother, I had one simple question: how do I make sure my child eats well every day? Even on days when I was sick or tired, my child still needed to eat.",
  paragraphs: [
    "Like many mothers, I felt guilty all the time. When I spoke to other mothers, I learnt we all had the same problem. Working mothers had no time to plan meals. Others spent hours guiding their cooks. And ready-made food was not good enough for our children.",
    "So I built what I wished I had. Mothers by Mothers makes fresh, healthy meals for children, made with children's nutrition experts.",
    "This is about more than food. It gives mothers back their time and peace of mind. It also gives women paid work and a chance to build something of their own.",
    "Raising a child was never meant to be one woman's job.",
  ],
  signature: "— Urvashi Parmar, Founder",
  mission: {
    headline: "Our mission and our solution",
    copy:
      "We are taking on the responsibility others avoided. No pre-mixes and no processed, shelf-stable products — we prepare fresh, wholesome, nutritionally balanced meals designed specifically for children.",
    points: [
      {
        title: "Fresh, never processed",
        copy:
          "Cooked to order for the delivery schedule. No pre-mixes, no shelf-stable packets, no preservative shortcuts.",
      },
      {
        title: "Developed with paediatric nutritionists",
        copy:
          "I partnered with expert paediatric nutritionists and spent months researching and testing recipes that are safe, balanced and genuinely delicious.",
      },
      {
        title: "Built to remove the guilt",
        copy:
          "The goal is simple: give mothers back their peace of mind, lessen the daily overwhelm, and end the constant mom guilt.",
      },
    ],
  },
  purpose: {
    headline: "Beyond food: a greater purpose",
    copy:
      "Mothers are expected to nourish the next generation, yet their labour is rarely appreciated or supported. We want to change that ecosystem entirely.",
    points: [
      {
        title: "For working mothers",
        copy:
          "A trusted, uncompromised service, so you can pursue your career without ever trading it against your child's health.",
      },
      {
        title: "For mothers seeking independence",
        copy:
          "Meaningful, paid work for women who were pushed out of the workforce by childcare — united around a shared and genuinely worthwhile cause.",
      },
    ],
  },
  belief: "It takes a village to raise a child. We’re here to be yours.",
  closing:
    "This service is for anyone who feeds a child — mothers, fathers, grandparents, guardians and caregivers alike.",
};

export const supportingMothers = {
  headline: "It takes a village — we're yours.",
  intro:
    "Mothers by Mothers is a business, not a campaign. The purpose behind it is straightforward: help families feed their children well, and create dependable, paid, skilled work for women who were pushed out of the workforce by childcare.",
  why: [
    "Give working mothers a trusted service, so a career never has to be traded against a child's health.",
    "Create real, paid employment for women whose careers were interrupted by childcare duties.",
    "Build a structured, quality-controlled kitchen network rather than an informal one.",
  ],
  standards: [
    "Standard recipes",
    "Ingredient specifications",
    "Hygiene procedures",
    "Portion standards",
    "Packaging processes",
    "Temperature controls where applicable",
    "Quality checks",
    "Training",
  ],
  standardsNote:
    "Anyone preparing food for a child through Mothers by Mothers works to the same written standards. Quality comes before expansion.",
};

export const legalPages = {
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated October 2026",
    sections: [
      {
        heading: "What we collect",
        body: "We collect the parent’s name, contact details, delivery address or school, and the child profile information you provide, including age category, dietary preferences and allergy information. We collect this only to prepare and deliver meals and to contact you about your subscription.",
      },
      {
        heading: "How we use it",
        body: "Your information is used to prepare the correct meal, deliver it to the correct place, respond to your enquiries and send order confirmations by email and WhatsApp where you have consented.",
      },
      {
        heading: "Children’s information",
        body: "Child information is provided by a parent or guardian and is used only for meal preparation and safety. We do not sell it and we do not use it for advertising.",
      },
      {
        heading: "Sharing",
        body: "We share information only with service providers who help us operate — for example a payment gateway or a delivery partner — and only as far as necessary.",
      },
      {
        heading: "Your choices",
        body: "You can ask us to correct or delete your information, or withdraw WhatsApp consent, by writing to us.",
      },
    ],
  },
  terms: {
    title: "Terms and Conditions",
    updated: "Last updated October 2026",
    sections: [
      { heading: "About these terms", body: "These terms apply to the use of this website and to any meal subscription purchased through it." },
      { heading: "Subscriptions", body: "Weekly and monthly lunch subscriptions are offered subject to area serviceability and kitchen capacity. We confirm every subscription before it starts." },
      { heading: "Accuracy of information", body: "You are responsible for the accuracy of the child profile, allergy and address information you provide. We rely on it to prepare and deliver meals." },
      { heading: "Changes to the menu", body: "Menus may change for reasons of ingredient availability or seasonality. We will tell you if a listed meal is substituted." },
      { heading: "Pricing", body: "Prices, delivery fees and taxes will be shown clearly before you confirm a subscription." },
    ],
  },
  delivery: {
    title: "Delivery Policy",
    updated: "Last updated October 2026",
    sections: [
      { heading: "Where we deliver", body: "We deliver to selected Pune pincodes. Serviceability is confirmed by the pincode checker on this website." },
      { heading: "Delivery days", body: "Lunches are delivered Monday to Friday, excluding declared holidays." },
      { heading: "Cut-off times", body: "Changes, skips and pauses must be requested before 8:00 pm on the previous day." },
      { heading: "School delivery", body: "School delivery depends on the school’s permission and our route capacity. Available schools are listed at checkout." },
      { heading: "Missed deliveries", body: "If someone is not available to receive a delivery, our team will contact you. Meals left unattended cannot be replaced." },
    ],
  },
  refunds: {
    title: "Cancellation and Refund Policy",
    updated: "Last updated October 2026",
    sections: [
      { heading: "Cancelling a subscription", body: "You may cancel a subscription at any time. Cancellation takes effect from the next unpaid cycle." },
      { heading: "Skips and pauses", body: "Skipped days requested before the cut-off time are credited to your account or extended onto your plan." },
      { heading: "Refunds", body: "Where a meal is not delivered because of a fault on our side, we credit that meal to your next cycle or refund it. Refunds to the original payment method are processed within 5 to 7 working days." },
      { heading: "Food quality concerns", body: "Tell us the same day. We will review it with the kitchen and put it right." },
    ],
  },
  allergy: {
    title: "Allergy Disclaimer",
    updated: "Important — please read before ordering",
    sections: [
      { heading: "Shared kitchen", body: "Our kitchen handles milk products, wheat, peanuts, tree nuts, sesame, coconut and other common allergens. We cannot guarantee that any meal is entirely free from traces of an allergen." },
      { heading: "Severe allergies", body: "If your child has a severe or life-threatening allergy, you must contact our team before ordering. We may tell you that we cannot safely serve your child, and we would rather say that than take a risk." },
      { heading: "Your responsibility", body: "Please keep your child’s allergy information accurate and up to date in their profile, and check the allergen list on each meal card." },
      { heading: "Ingredient changes", body: "If an ingredient changes, we will update the meal card and notify subscribers whose profile lists a relevant allergen." },
    ],
  },
  foodSafety: {
    title: "Food Safety Information",
    updated: "Last updated October 2026",
    sections: [
      { heading: "Licensing", body: "Our FSSAI licence number will be published here and in the footer once issued." },
      { heading: "Preparation", body: "Meals are prepared to documented recipes and hygiene procedures, with quality checks before dispatch." },
      { heading: "Packaging and transport", body: "Meals are packed in food-grade containers and carried in insulated bags on every route." },
      { heading: "At home", body: "Eat the meal soon after delivery where possible. Otherwise refrigerate below 5°C and consume the same day. Reheat until hot throughout." },
    ],
  },
};
